import { useCallback } from "react";
import type { SendEmailFormData } from "@/components/shared/formElements/SendEmailForm";
import { toSendEmailTaskParameters } from "@/features/email/utils/send-email-task-parameters";
import {
  CLASS_TASK_ACTIONS,
  CLASS_TASK_ENTITY,
} from "@/features/tasks/constants";
import { useCreateTask } from "@/features/tasks/hooks";
import { useTenantCurrencyNumeric } from "@/hooks";

export function useClassTaskSubmit() {
  const currency = useTenantCurrencyNumeric();
  const { createTaskAsync, isPending } = useCreateTask();

  const submitSendEmail = useCallback(
    async (classIds: number[], data: SendEmailFormData) => {
      await createTaskAsync({
        entityName: CLASS_TASK_ENTITY,
        actionName: CLASS_TASK_ACTIONS.sendEmail,
        selectedIds: classIds,
        executionParameters: toSendEmailTaskParameters(data),
        files: data.attachments,
      });
    },
    [createTaskAsync],
  );

  const submitAddFee = useCallback(
    async (
      classIds: number[],
      amount: number,
      transactionCode: string,
      note?: string,
    ) => {
      const trimmedNote = note?.trim();
      await createTaskAsync({
        entityName: CLASS_TASK_ENTITY,
        actionName: CLASS_TASK_ACTIONS.addFee,
        selectedIds: classIds,
        executionParameters: {
          Currency: currency,
          Amount: amount,
          TransactionCode: transactionCode,
          ...(trimmedNote ? { Note: trimmedNote } : {}),
        },
      });
    },
    [createTaskAsync, currency],
  );

  const submitAddCredit = useCallback(
    async (classIds: number[], amount: number, transactionCode: string) => {
      await createTaskAsync({
        entityName: CLASS_TASK_ENTITY,
        actionName: CLASS_TASK_ACTIONS.addCredit,
        selectedIds: classIds,
        executionParameters: {
          Currency: currency,
          Amount: amount,
          TransactionCode: transactionCode,
        },
      });
    },
    [createTaskAsync, currency],
  );

  const submitDeactivate = useCallback(
    async (classIds: number[]) => {
      await createTaskAsync({
        entityName: CLASS_TASK_ENTITY,
        actionName: CLASS_TASK_ACTIONS.bulkDeactivate,
        selectedIds: classIds,
      });
    },
    [createTaskAsync],
  );

  return {
    submitSendEmail,
    submitAddFee,
    submitAddCredit,
    submitDeactivate,
    isPending,
  };
}
